import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useAppContext } from "../../../shared/contexts/app-context/components/AppContext";
import {
    useAppSidePanelContextProvider
} from "../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import { OptionInfoDto } from "../types/OptionInfoDto";
import { LocalStorageConstants } from "../../../shared/constants/LocalStorageConstants";
import { OptionsTable } from "../components/options-table/OptionsTable";
import { UnderlyingAssetsTable } from "../components/underlying-assets-table/UnderlyingAssetsTable";
import { dateTimeConversionService } from "../../../shared/services/DateTimeConversionService";
import { ExpirationSelector } from "../../../screens/shared/controls";
import { ImpliedVolatilityChart } from "../../../screens/implied-volatility/widgets/ImpliedVolatilityChart";
import { AssetType } from "../../../shared/types/AssetType";
import { MultipleStrikeSelector } from "../../../screens/implied-volatility/controls/MultipleStrikeSelector";
import {
    optionParametersSelectorManager
} from "../../../screens/positions-and-orders/utils/OptionParametersSelectorManager";

export default function OptionsTablePage() {
    const [optionInfoData, setOptionInfoData] = useState<OptionInfoDto[]>([]);
    const [selectedOptionType, setSelectedOptionType] = useState<AssetType | null>(null);
    const [expirations, setExpirations] = useState<string[]>([]);
    const [selectedExpiration, setSelectedExpiration] = useState<string | null>(null);
    
    const [strikes, setStrikes] = useState<number[]>([]);
    const [selectedStrike, setSelectedStrike] = useState<number | null>(null);
    
    const {
        marketDataDto,
    } = useAppContext();

    const {
        addOptionToPortfolio,
        addFutureToPortfolio,
    } = useAppSidePanelContextProvider();
    
    const handleTypeFilter = (
        event: React.MouseEvent<HTMLElement>,
        newValue: AssetType,
    ) => {
        if (!marketDataDto) return;
        applyTypeFilter(marketDataDto!.OptionInfos, newValue);
    };
    
    useEffect(() => {
        if (marketDataDto && marketDataDto.OptionInfos) {
            setOptionInfoData(marketDataDto.OptionInfos);
            const storedSelectedOptionType = localStorage.getItem(LocalStorageConstants.SelectedOptionTypeName);

            const assetType = storedSelectedOptionType !== null
                ? (Number(storedSelectedOptionType) as AssetType)
                : AssetType.Call;
            
            applyTypeFilter(marketDataDto.OptionInfos, assetType);
            const availableExpirations = marketDataDto.Expirations.map(x => dateTimeConversionService.convertDateToLongFormattedString(x));
            setExpirations(availableExpirations);
            
            const currentExpiration = localStorage.getItem(LocalStorageConstants.SelectedExpirationName);
            const selectedExpiration = currentExpiration === null
                ? availableExpirations[0]
                : currentExpiration;
            
            setSelectedExpiration(selectedExpiration);
            setAvailableStrikes(assetType, selectedExpiration);

            const currentStrike = localStorage.getItem(LocalStorageConstants.SelectedStrikeName);
            setSelectedStrike(Number(currentStrike));
        }
    }, [marketDataDto]);

    function applyTypeFilter(initialOptionInfoData: OptionInfoDto[], newSelectedType: AssetType) {
        setSelectedOptionType(newSelectedType);
        localStorage.setItem(LocalStorageConstants.SelectedOptionTypeName, newSelectedType.toString());
        setOptionInfoData(initialOptionInfoData?.filter(x => x.Type == newSelectedType) ?? []);
        setAvailableStrikes(newSelectedType, selectedExpiration!);
    }

    function handleExpirationSelection(newSelectedExpiration: string) {
        setSelectedExpiration(newSelectedExpiration);
        localStorage.setItem(LocalStorageConstants.SelectedExpirationName, newSelectedExpiration);
        setAvailableStrikes(selectedOptionType!, newSelectedExpiration);
    }

    const setAvailableStrikes = (optionType: AssetType, expiration: string) => {
        const availableStrikes = optionParametersSelectorManager.getStrikes(
            marketDataDto!.OptionInfos, optionType,
            dateTimeConversionService.parseToUtcDate(expiration!),
            (x, y) => x > y ? 1 : -1)

        setStrikes(availableStrikes);
    }
    
    const handleStrikeSelection = (newSelectedValue: number) => {
        setSelectedStrike(newSelectedValue);
        localStorage.setItem(LocalStorageConstants.SelectedStrikeName, newSelectedValue.toString());
    }
    
    if (marketDataDto === undefined) {
        return null;
    }
    
    return (
       <Stack
           direction="column"
           spacing={2}
       >
           <UnderlyingAssetsTable
               underlyingAsset={marketDataDto.BaseAsset}
               spotPrice={marketDataDto.UnderlyingPrice}
               futuresPrice={marketDataDto.FuturePrice}
               addFutureToPortfolio={addFutureToPortfolio}
           />
           <Typography variant="subtitle1">
               <b>Options strikes</b> (total {marketDataDto?.OptionInfos.length ?? 0} available)
           </Typography>
           <Stack direction="column" spacing={2}>
               <Stack direction="row" spacing={3} alignItems="flex-end">
                   <ExpirationSelector
                       expirations={expirations}
                       value={selectedExpiration!}
                       onValueChange={handleExpirationSelection} />
                   <ToggleButtonGroup
                       color="standard"
                       value={selectedOptionType}
                       exclusive
                       aria-label="Platform"
                       onChange={handleTypeFilter}
                       style={{ height: '30px' }}
                   >
                       <ToggleButton value={AssetType.Call} color="standard" style={{ fontWeight: 'bold' }}>Call</ToggleButton>
                       <ToggleButton value={AssetType.Put} color="standard" style={{ fontWeight: 'bold' }}>Put</ToggleButton>
                   </ToggleButtonGroup>
                   <MultipleStrikeSelector
                       strikes={strikes}
                       value={selectedStrike!}
                       onValueChange={handleStrikeSelection}
                   />
               </Stack>
               <ImpliedVolatilityChart
                   selectedExpiration={selectedExpiration!}
                   selectedOptionType={selectedOptionType!}
                   selectedStrike={selectedStrike!}
               />
               <OptionsTable
                   marketDataDto={marketDataDto}
                   selectedExpiration={selectedExpiration}
                   optionInfoData={optionInfoData}
                   addOptionToPortfolio={addOptionToPortfolio}
               />
           </Stack>
       </Stack>
    );
}